<template>
  <!-- Portal root — moved to document.body on mount -->
  <div class="ec-drawer-portal">
  <transition name="ec-drawer">
    <div v-if="value" class="ec-drawer-overlay" @mousedown.self="close">
      <div
        class="ec-drawer-content"
        :class="{ 'ec-drawer-content--fullscreen': isFullscreen }"
        :style="drawerStyle"
      >
        <!-- Resize handle -->
        <div
          v-if="resizable && !isFullscreen"
          class="ec-drawer-resize-handle"
          @mousedown="onResizeStart"
        >
          <div class="ec-drawer-resize-handle__bar" />
        </div>

        <!-- Header -->
        <div class="ec-drawer-header">
          <slot name="header">
            <span v-if="title" class="ec-drawer-title">{{ title }}</span>
          </slot>
          <div class="ec-drawer-header__actions">
            <button
              v-if="resizable"
              type="button"
              class="ec-drawer-close"
              :title="isFullscreen ? 'Réduire' : 'Plein écran'"
              @click="isFullscreen = !isFullscreen"
            >
              <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path fill-rule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707zm4.344-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707z"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path fill-rule="evenodd" d="M.172 15.828a.5.5 0 0 0 .707 0l4.096-4.096V14.5a.5.5 0 1 0 1 0v-3.975a.5.5 0 0 0-.5-.5H1.5a.5.5 0 0 0 0 1h2.768L.172 15.12a.5.5 0 0 0 0 .708zm15.656-15.656a.5.5 0 0 0-.707 0l-4.096 4.096V1.5a.5.5 0 1 0-1 0v3.975a.5.5 0 0 0 .5.5H14.5a.5.5 0 0 0 0-1h-2.768L15.828.878a.5.5 0 0 0 0-.707z"/>
              </svg>
            </button>
            <button type="button" class="ec-drawer-close" aria-label="Fermer" @click="close">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="ec-drawer-body">
          <slot />
        </div>

        <!-- Footer -->
        <div v-if="$slots.footer" class="ec-drawer-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </transition>
  </div>
</template>

<script>
export default {
  name: 'EcDrawer',

  model: {
    prop: 'value',
    event: 'input',
  },

  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
    width: { type: String, default: '480px' },
    minWidth: { type: String, default: '320px' },
    resizable: { type: Boolean, default: false },
    fullscreen: { type: Boolean, default: false },
  },

  data() {
    return {
      isFullscreen: this.fullscreen,
      currentWidth: null,
      isResizing: false,
    }
  },

  computed: {
    drawerStyle() {
      if (this.isFullscreen) return { width: '100vw' }
      var w = this.currentWidth ? this.currentWidth + 'px' : this.width
      return { width: w }
    },
  },

  mounted() {
    // Teleport to body so the drawer isn't clipped by parent overflow/stacking context
    document.body.appendChild(this.$el)
  },

  watch: {
    value(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
        this.currentWidth = null
        this.isFullscreen = this.fullscreen
      }
    },
  },

  beforeDestroy() {
    // Remove portal element from body
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
    document.body.style.overflow = ''
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  },

  methods: {
    close() {
      this.$emit('input', false)
    },

    onResizeStart(e) {
      if (this.isFullscreen) return
      e.preventDefault()
      var self = this
      self.isResizing = true

      var startX = e.clientX
      var drawerEl = e.target.closest('.ec-drawer-content')
      var startWidth = drawerEl ? drawerEl.offsetWidth : parseInt(self.width)
      var minW = parseInt(self.minWidth)

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      function onMouseMove(ev) {
        var delta = startX - ev.clientX
        var newWidth = Math.max(minW, Math.min(startWidth + delta, window.innerWidth * 0.95))
        self.currentWidth = newWidth
      }

      function onMouseUp() {
        self.isResizing = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
  },
}
</script>

<style>
.ec-drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: flex-end;
}

.ec-drawer-content {
  position: relative;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.ec-drawer-resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ec-drawer-resize-handle__bar {
  width: 2px;
  height: 40px;
  border-radius: 2px;
  background: var(--ec-border-color, #e2e8f0);
}

.ec-drawer-resize-handle:hover .ec-drawer-resize-handle__bar {
  background: var(--ec-primary, #3b82f6);
}

.ec-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--ec-border-color, #e2e8f0);
  flex-shrink: 0;
}

.ec-drawer-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--ec-text-color, #1f2937);
}

.ec-drawer-header__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.ec-drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  border-radius: 6px;
  cursor: pointer;
  color: var(--ec-text-muted, #6b7280);
  transition: background 0.15s, color 0.15s;
  padding: 0;
}

.ec-drawer-close:hover {
  background: var(--ec-bg-muted, #f3f4f6);
  color: var(--ec-text-color, #1f2937);
}

.ec-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.ec-drawer-footer {
  padding: 12px 20px;
  border-top: 1px solid var(--ec-border-color, #e2e8f0);
  flex-shrink: 0;
}

/* Transition */
.ec-drawer-enter-active,
.ec-drawer-leave-active {
  transition: opacity 0.2s ease;
}
.ec-drawer-enter-active .ec-drawer-content,
.ec-drawer-leave-active .ec-drawer-content {
  transition: transform 0.25s ease;
}
.ec-drawer-enter .ec-drawer-content,
.ec-drawer-leave-to .ec-drawer-content {
  transform: translateX(100%);
}
.ec-drawer-enter,
.ec-drawer-leave-to {
  opacity: 0;
}
</style>
